import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import _ from "underscore";

import DRGCollection from "../../../collections/chart-reviews/drgs";
import ClaimsCollection from "../../../collections/chart-reviews/claims";
import ClaimModel from "../../../models/chart-reviews/claim";
import DRGFormModel from "../../../models/chart-reviews/drg-form";

import PopoverBehavior from "../../../behaviors/popover";
import AutoCompleteBehavior from "../../../behaviors/kendo-ui/autocomplete";
import ComboBoxBehavior from "../../../behaviors/kendo-ui/combobox";
import DatePickerBehavior from "../../../behaviors/kendo-ui/datepicker";
import ChosenBehavior from "../../../behaviors/chosen";

import DataSource from "../../../datasources/icd";

import {
  encodeData,
  dateFormatter,
  INPUT_DATE_FORMAT,
  OUTPUT_DATE_FORMAT,
} from "../../../utils";
import templates from "../../../utils/templates";
import environment from "../../../common/environment";

const channel = Radio.channel("global");

const Row = Marionette.ItemView.extend({
  tagName: "tr",
  template: templates.find("chart_reviews", "chart_edit_drg_add_diag"),
  behaviors: [
    {
      behaviorClass: PopoverBehavior,
      opts: {
        html: true,
        placement: "auto left",
      },
    },
  ],
});

const DEFAULTS = {
  tagName: "form",
  template: templates.find("chart_reviews", "chart_edit_drg_add_simple_layout"),
  childView: Row,
  childViewContainer: "tbody",
};

class Form extends Marionette.CompositeView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  behaviors() {
    return [
      {
        behaviorClass: ChosenBehavior,
        selector: "#diagnosisAttributes",
        opts: {
          width: "100%",
        },
      },
      {
        behaviorClass: DatePickerBehavior,
        selector: "#fromDateOfService",
      },
      {
        behaviorClass: DatePickerBehavior,
        selector: "#throughDateOfService",
      },
      {
        behaviorClass: AutoCompleteBehavior,
        selector: "#diagCode",
        opts: {
          dataSource: this._getDiagCodeDatasource(),
          dataTextField: "code",
          template: `
						<span>
							<span class="text-muted small">(#: kendo.toString(score/100, "p2") #)</span>
							<strong>#:code#</strong> - #:desc#
						</span>
					`,
          noDataTemplate: "No matches found",
          footerTemplate:
            "Displaying <strong>#: instance.dataSource.data().length #</strong> of #: kendo.toString(instance.dataSource.total(), 'n0') # matches found",
          // handle start of filtering by user
          filtering() {
            // update model to nullify previously selected code
            channel.trigger("validation:diagCode", false, null);
          },
          // handle user selection by keyboard or mouse
          select(e) {
            // get selected code
            const diagCode = e.dataItem.code;

            // update field
            this.value(diagCode);

            // update model
            channel.trigger("validation:diagCode", true, diagCode);
          },
          // handle onblur action
          change() {
            // get current input value
            const value = this.value();

            // there is a chance the value was modified, compare it with the one in the model
            const isValid = channel.request("isDiagCodeValid", value);
            if (!isValid) {
              // invalid, update field to reflect reality
              this.value(null);
              // update model to nullify previously selected code
              channel.trigger("validation:diagCode", false, null);
            }
          },
        },
      },
      {
        behaviorClass: ComboBoxBehavior,
        selector: "#claimNumber",
        dataAttribute: "claimsList",
        opts: {
          enable: false,
          placeholder: "Select or enter a claim...",
          dataTextField: "claimNbr",
          dataValueField: "claimNbr",
          template: `
						<table class="table-condensed claims-table">
							<tr>
								<td class="title">Claim \\\\#</td>
								<td>#:claimNbr#</td>
							</tr>
							<tr>
								<td class="title">Type</td>
								<td>#:claimType#</td>
							</tr>
							<tr>
								<td class="title">Provider</td>
								<td>#:providerNbr#<br># if (providerFirstName) { # #:providerFirstName# # } # #:providerLastName#</td>
							</tr>
							<tr>
								<td class="title">Plan ID</td>
								<td>#:commercialPlanId#</td>
							</tr>
							<tr>
								<td class="title">DOS</td>
								<td>#:fromDos# - #:throughDos#</td>
							</tr>
							# if (procCode) { #
							<tr>
								<td class="title">PROC CODE</td>
								<td>#:procCode#</td>
							</tr>
							# } #
						</table>
					`,
          noDataTemplate: "No claims found",
          change() {
            // determine selected commercial plan ID
            let commercialPlanId = null;
            if (this.selectedIndex > -1 && !this.typing) {
              commercialPlanId = this.dataSource
                .data()
                .at(this.selectedIndex).commercialPlanId;
            }
            // update commercial plan ID form field
            const field = this._form.find("#commercialPlanId");
            field && field.val(commercialPlanId);
          },
        },
      },
    ];
  }

  ui() {
    return {
      fromDate: "#fromDateOfService",
      thruDate: "#throughDateOfService",
      mappedHCCs: "input[name=mappedHCCs]",
      diagCode: "#diagCode",
      claimNumber: "#claimNumber",
      commercialPlanId: "#commercialPlanId",
      action: "#action",
      diagnosisAttributes: "#diagnosisAttributes",
      note: "#note",
      // read only fields
      mappedHCCsText: "td.mapped-hccs",
      statusMessage: "div.status-message",
      // buttons
      addBtn: "button.add-drg",
      removeBtn: "button.remove-drg",
      saveValidDRGs: "button.save-valid-drgs",
      returnToChart: "button.return-to-chart",
    };
  }

  events() {
    return {
      submit: _.debounce(this._onSubmit, 200),
      "change @ui.fromDate": "_onUIFromDateChange",
      "change @ui.thruDate": "_onUIThruDateChange",
      "keyup @ui.diagCode": "_onUIDiagCodeChange",
      "change @ui.claimNumber": "_onUIClaimNumberChange",
      "change @ui.commercialPlanId": "_onUICommercialPlanIdChange",
      // "change @ui.action": "_handleActionSelection",
      // buttons
      "click @ui.removeBtn": _.debounce(this._removeDRG, 200),
      "click @ui.saveValidDRGs": _.debounce(this._saveValidDGRs, 200),
    };
  }

  initialize() {
    // grab chart review and member Ids from original model
    this.chartReviewId = this.model.getId();
    this.memberId = this.model.getSelectedMember().getId();

    this.canSubmitCollection = false;

    // create empty model to track DRG input
    this.emptyModel = {
      reviewers: this.model.getUsers(),
      canSubmitCollection: this.canSubmitCollection,
      diagMinDate: this.model.getDiagMinDate(),
      diagMaxDate: this.model.getDiagMaxDate(),
      didFetchMappedHCCs: false,
      diagnosisAttributesList: this.model.getDiagnosisAttributesList().toJSON(),
    };

    // create empty collection for DRG creation
    const Collection = DRGCollection.extend({
      url: environment.getEndpoint("chart-reviews:drg-adds", {
        chartId: this.chartReviewId,
      }),
    });
    this.collection = new Collection();

    this.listenTo(this.collection, "update", this._onCollectionUpdate);
    this.listenTo(this.collection, "reset", this._onCollectionUpdate);

    // create form model
    this._createFormModel(this.emptyModel);

    // console.log(this.collection);
    // console.log(this.model);
  }

  // Kendo DS configured for Elasticsearch
  _getDiagCodeDatasource() {
    return new DataSource({
      diagCode: this.ui().diagCode,
      thruDate: this.ui().thruDate,
    });
  }

  _createFormModel(data) {
    // create empty form model
    this.model = new DRGFormModel();

    // listen for changes to the model
    this.listenTo(this.model, "change", _.debounce(this._onModelChange, 100));
    this.listenTo(
      this.model,
      "change:statusMessage",
      this._onStatusMessageChange
    );
    this.listenTo(
      this.model,
      "change:canFetchMappedHCCs",
      this._canFetchMappedHCCs
    );
    this.listenTo(this.model, "change:canFetchClaims", this._canFetchClaims);
    this.listenTo(this.model, "change:claimsList", this._onClaimsListChange);
    this.listenTo(this.model, "change:claimNumber", this._onClaimNumberChange);

    // finally, populate model with initial dataset
    this.model.set(data);
  }

  _resetForm() {
    // clear non date fields
    this._uiClearDiagCode(true);
    this.ui.mappedHCCsText.html(null);
    this._uiClearClaimNumber();
    this.ui.commercialPlanId.val(null);
    this.ui.action.val(null);
    this.ui.note.val(null);
  }

  _uiClearDiagCode(focus = false) {
    // update on model
    this.model.set({
      diagCode: null,
      isValidDiagCode: false,
    });

    this.ui.diagCode.val(null);
    focus && this.ui.diagCode.focus();
  }

  _uiClearClaimNumber() {
    // update on model
    this.model.set({
      claimNumber: null,
    });

    const combobox = this.ui.claimNumber.getKendoComboBox();
    combobox.value(null);
  }

  _onModelChange(model) {
    const m = model.toJSON();

    // update DOS
    this.ui.fromDate.val(m.fromDateOfService);
    this.ui.thruDate.val(m.throughDateOfService);

    this.ui.diagCode.attr("disabled", !m.canEditDiagCode);
    this.ui.claimNumber.getKendoComboBox().enable(m.canEditClaimNumber);
    this.ui.commercialPlanId.attr("disabled", !m.canEditCommercialPlanId);

    this.ui.action.attr("disabled", !m.canEditActions);
    // toggle available options in dropdown
    if (m.canEditActions) {
      const { canApproveForSubmission, canStartEvaluation } = m;
      const flags = { canApproveForSubmission, canStartEvaluation };
      const field = this.ui.action;
      const options = field.get(0).options; // returns an HTMLOptionsCollection
      [...options].forEach((option) => {
        if (flags[option.className]) {
          option.disabled = !flags[option.className];
        }
      });
    } else {
      const field = this.ui.action;
      const options = field.get(0).options; // returns an HTMLOptionsCollection
      // disable options with a className
      [...options].forEach((option) => {
        if (!!option.className) {
          option.disabled = true;
        }
      });
    }

    // toggle notes field
    this.ui.note.attr("disabled", !m.canEditNote);

    // toggle diag attributes field
    this.ui.diagnosisAttributes.attr("disabled", !m.canEditDiagnosisAttributes);

    // toggle single diag add button
    const { valid, didFetchMappedHCCs } = m;
    const addBtnDisabled = !valid || !didFetchMappedHCCs;
    this.ui.addBtn.attr("disabled", addBtnDisabled);

    // toggle submit button
    this.ui.saveValidDRGs.attr("disabled", !m.canSubmitCollection);

    // update read only props
    this.ui.mappedHCCsText.html(m.mappedHCCs);

    // update hidden inputs
    this.ui.mappedHCCs.val(m.mappedHCCs);

    this.triggerMethod("Reset");
    this.triggerMethod("ContentRendered");
  }

  _onStatusMessageChange(model, value) {
    let message = "This Diag is ready to be added to your list";
    let className = "text-success";
    let icon = "fa-check";

    const valid = model.get("valid");
    if (!valid) {
      message = value;
      className = "text-info";
      icon = "fa-info-circle";
    }

    const html = `
			<span class="${className}">
				<i class="fa fa-fw fa-lg ${icon}"></i>
				${message}
			</span>
		`;

    this.ui.statusMessage.html(html);
  }

  _canFetchMappedHCCs(model, canFetch) {
    if (canFetch) {
      this._fetchMappedHCCs();
    } else {
      this.model.set({
        mappedHCCs: null,
      });
    }
  }

  _canFetchClaims(model, canFetch) {
    if (canFetch) {
      this._fetchClaims();
    } else {
      this.model.set({
        claimsList: this.emptyModel.claimsList,
      });
      this._uiClearClaimNumber();
    }
  }

  _onCollectionUpdate(coll) {
    // count error free models in collection, convert to boolean
    this.canSubmitCollection = !!coll.where({ errors: null }).length;
    this.model.set("canSubmitCollection", this.canSubmitCollection);
  }

  _onUIFromDateChange() {
    const field = this.ui.fromDate;
    const dateString = field.val().trim();
    const value =
      dateString !== ""
        ? dateFormatter(dateString, OUTPUT_DATE_FORMAT, INPUT_DATE_FORMAT)
        : null;

    // update both dates on UI
    field.val(value);
    this.ui.thruDate.val(value);

    // update both dates on model
    this.model.set({
      fromDateOfService: value,
      throughDateOfService: value,
    });
  }

  _onUIThruDateChange() {
    const field = this.ui.thruDate;
    const dateString = field.val().trim();
    const value =
      dateString !== ""
        ? dateFormatter(dateString, OUTPUT_DATE_FORMAT, INPUT_DATE_FORMAT)
        : null;

    // update on UI
    field.val(value);

    // update on model
    this.model.set({
      throughDateOfService: value,
    });
  }

  _onUIDiagCodeChange() {
    const field = this.ui.diagCode;
    let value = field.val().trim().toUpperCase();
    field.val(value);
    if (value === "") {
      channel.trigger("validation:diagCode", false, null);
    }
  }

  _onUIClaimNumberChange() {
    let value = this.ui.claimNumber.val().trim();

    // update on model
    this.model.set({
      claimNumber: value !== "" ? value : null,
    });
  }

  _onUICommercialPlanIdChange() {
    const field = this.ui.commercialPlanId;
    let value = field.val().trim().toUpperCase();
    field.val(value);

    this.model.set({
      commercialPlanId: value !== "" ? value : null,
    });
  }

  _fetchClaims() {
    const { fromDateOfService, throughDateOfService } = this.model.toJSON();
    $.when(
      $.ajax({
        url: environment.getEndpoint("member:claims", { id: this.memberId }),
        data: {
          fromDateOfService,
          throughDateOfService,
        },
      })
    ).then(
      // on success
      (claims) => {
        // update the form model with these values
        this.model.set({
          claimsList: new ClaimsCollection(claims),
        });
      },
      // on failure
      () => {
        // show error
        channel.trigger("status:show", {
          autoDismiss: false,
          type: "danger",
          message: "We were unable to retrieve Claims. Please try again later.",
        });
      }
    );
  }

  _fetchMappedHCCs() {
    const { diagCode, throughDateOfService } = this.model.toJSON();
    if (diagCode && throughDateOfService) {
      $.when(
        $.ajax({
          url: environment.getEndpoint("member:mapped-hccs", {
            id: this.memberId,
          }),
          data: {
            diagCode,
            throughDateOfService,
          },
        })
      ).then(
        // on success
        (mappedHCCs) => {
          // update the form model with these values
          this.model.set({
            mappedHCCs,
            didFetchMappedHCCs: true,
          });
        },
        // on failure
        () => {
          // show error
          channel.trigger("status:show", {
            autoDismiss: false,
            type: "danger",
            message:
              "We were unable to retrieve Mapped HCCs. Please try again later.",
          });
        }
      );
    }
  }

  _onClaimsListChange() {
    this._notifyBehaviors();
  }

  _onClaimNumberChange() {
    // reset commercialPlanId
    this.model.set("commercialPlanId", null);

    // grab current list of claims
    const claimsList = this.model.getClaimsList();

    // grab current claim number
    const claimNumber = this.model.getClaimNumber();
    if (claimsList && !_.isEmpty(claimNumber)) {
      // find it on current list
      let claim = claimsList.get(claimNumber);
      if (claim) {
        this.model.set("commercialPlanId", claim.get("commercialPlanId"));
      } else {
        // create Claim model
        const Model = ClaimModel.extend({
          urlRoot: environment.getEndpoint("claim", { id: claimNumber }),
          initialize() {
            this.promise = this.fetch();
          },
        });
        // fetch it from API
        claim = new Model();

        // on success, set the commercialPlanId
        claim.promise.done((response, success) => {
          const commercialPlanId = success
            ? response[0].commercialPlanId
            : null;
          this.model.set({ commercialPlanId });
          this.ui.commercialPlanId.val(commercialPlanId);
        });
      }
    }
  }

  // handles the addition of one DRG to cart
  _onSubmit(e) {
    e.preventDefault();

    // extract submitted form data
    const formData = Backbone.Syphon.serialize(this);
    // clean up fields added by KendoUI
    delete formData.claimNumber_input;

    // determine action
    if (formData.action !== "") {
      formData[formData.action] = true;
    }
    delete formData.action;

    // create diag attributes display label
    const { diagnosisAttributes } = formData;
    if (diagnosisAttributes && _.isArray(diagnosisAttributes)) {
      const list = this.model.getDiagnosisAttributesList();
      const labels = diagnosisAttributes
        .map((key) => list.find({ key }))
        .map((attr) => {
          const [code] = attr.getValue().split("-");
          return code;
        });

      formData.diagnosisAttributesDisplay = labels.join(", ");
    }

    // create key
    const _key = `${formData.diagCode}-${formData.fromDateOfService}-${formData.throughDateOfService}`;
    const key = encodeData(_key);

    // check collection for key
    const exists = this.collection.get(key);
    if (exists) {
      // let user know this combination of dates and diag code exists
      channel.trigger("status:show", {
        type: "warning",
        autoDismissTimeout: 5000,
        message:
          "Your list of new Diags already contains this DOS and Diag combination.",
      });
    } else {
      // use compound key as Id
      formData.id = key;

      // insert data into collection
      this.collection.add(formData);

      // create copy of empty model with from/thru dates from previous DRG
      const { fromDateOfService, throughDateOfService, diagnosisAttributes } =
        formData;
      const data = Object.assign(this.emptyModel, {
        fromDateOfService,
        throughDateOfService,
        diagnosisAttributes,
        canSubmitCollection: this.canSubmitCollection,
      });

      // create new form model
      this._createFormModel(data);

      // reset ui form
      this._resetForm();
    } // end uniqueness check
  }

  _removeDRG(e) {
    const key = $(e.currentTarget).data("key");
    const match = this.collection.get(key);
    if (match) {
      this.collection.remove(match);
    }
  }

  // handles the submission of the collection of DRGs to the API
  _saveValidDGRs() {
    // clear previous error messages
    this.collection.map((model) => {
      model.set("errors", null);
    });

    this.collection.save({
      success: this._onSync.bind(this),
      error: this._onSyncFailure.bind(this),
    });
  }

  _onSync() {
    // clear the collection
    this.collection.reset();

    // let edit tab know its model is out of date
    this._notifyOutdatedChartModel();

    // show error
    channel.trigger("status:show", {
      type: "success",
      message: "Your Diags have been created successfully.",
    });

    // switch to chart edit tab
    this.ui.returnToChart.click();
  }

  _onSyncFailure(jqXHR) {
    // reset collection with bad DRGs
    this.collection.reset(JSON.parse(jqXHR.responseText));

    // let edit tab know its model is out of date
    this._notifyOutdatedChartModel();

    // show error
    channel.trigger("status:show", {
      autoDismissTimeout: 5000,
      type: "warning",
      message:
        "Some of your Diags could not be created. Hover over the warning icon for details.",
    });
  }

  _notifyBehaviors() {
    this.triggerMethod("ModelChange", this.model);
  }

  _notifyOutdatedChartModel() {
    channel.trigger("notify:model:out-of-date");
  }
}

export { Form as default };
